/**
 * Weight Ticket Application Service
 * Transforms weight ticket data from backend DTOs into UI-ready view models
 *
 * Following DDD Principles:
 * - Pure transformation layer (DTO → ViewModel)
 * - NO business logic (backend owns approval rules, compliance validation)
 * - Formatting and UI calculations only (backend calculates net weight, we just format it)
 * - Single responsibility: prepare ticket data for display
 */

import { serviceProviderService } from '@/services/service-provider.service';
import type {
  WeightTicket,
  TicketFilters,
  TicketReportParams,
} from '@/services/service-provider.service';
import type {
  TicketCardViewModel,
  TicketDetailViewModel,
  TicketListViewModel,
  TicketFiltersViewModel,
  PendingReviewViewModel,
  TicketReportViewModel,
} from './view-models/WeightTicketViewModel';

/**
 * Weight Ticket Application Service
 * Handles weight ticket data transformation and list management
 */
export class WeightTicketApplicationService {
  /**
   * Get ticket list with optional filters
   */
  async getTicketList(filters?: TicketFilters): Promise<TicketListViewModel> {
    const response = await serviceProviderService.getTickets(filters);

    // Transform to view models
    const ticketCards = response.tickets.map((ticket) =>
      this.toTicketCardViewModel(ticket)
    );

    // Build filters view model
    const filtersViewModel = this.buildFiltersViewModel(
      response.tickets,
      filters
    );

    // Count by status
    const statusCounts = response.tickets.reduce(
      (acc, ticket) => {
        acc[ticket.status] = (acc[ticket.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      tickets: ticketCards,
      filters: filtersViewModel,
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: ticketCards.length,
        itemsPerPage: ticketCards.length,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      summary: {
        displayText: this.buildSummaryText(
          ticketCards.length,
          response.total
        ),
        totalCount: response.total,
        pendingReview: statusCounts.pending || 0,
        approved: statusCounts.approved || 0,
        rejected: statusCounts.rejected || 0,
      },
      isLoading: false,
      isEmpty: ticketCards.length === 0,
      emptyStateMessage: this.getEmptyStateMessage(filters?.status),
    };
  }

  /**
   * Get single ticket detail
   */
  async getTicketDetail(ticketId: string): Promise<TicketDetailViewModel> {
    const ticket = await serviceProviderService.getTicket(ticketId);
    return this.toTicketDetailViewModel(ticket);
  }

  /**
   * Get tickets pending review
   */
  async getPendingReview(): Promise<PendingReviewViewModel> {
    const response = await serviceProviderService.getPendingReviewTickets();

    const ticketCards = response.map((ticket) =>
      this.toTicketCardViewModel(ticket)
    );

    // Find oldest ticket
    let oldestTicket: { id: string; daysOld: number } | undefined;
    if (ticketCards.length > 0) {
      const oldest = response.reduce((prev, current) =>
        new Date(prev.created_at).getTime() < new Date(current.created_at).getTime()
          ? prev
          : current
      );
      const daysOld = this.calculateDaysElapsed(
        new Date(oldest.created_at * 1000).toISOString()
      );
      oldestTicket = { id: oldest.id, daysOld };
    }

    return {
      tickets: ticketCards,
      count: ticketCards.length,
      oldestTicket,
    };
  }

  /**
   * Get ticket report for analytics
   */
  async getTicketReport(
    params: TicketReportParams
  ): Promise<TicketReportViewModel> {
    const report = await serviceProviderService.getTicketReport(params);

    return {
      dateRange: {
        start: this.formatDate(params.start_date),
        end: this.formatDate(params.end_date),
      },
      summary: {
        totalTickets: report.total_tickets,
        totalWeight: this.formatWeight(report.total_weight_lbs),
        byMaterial: report.by_material.map((item) => ({
          material: item.material_type,
          weight: this.formatWeight(item.weight_lbs),
          percentage: Math.round(
            (item.weight_lbs / report.total_weight_lbs) * 100
          ),
        })),
        byStatus: report.by_status.map((item) => ({
          status: this.capitalizeStatus(item.status),
          count: item.count,
        })),
      },
      chartData: {
        materialDistribution: report.by_material.map((item) => ({
          name: item.material_type,
          value: item.weight_lbs,
        })),
        statusDistribution: report.by_status.map((item) => ({
          name: this.capitalizeStatus(item.status),
          value: item.count,
        })),
        weightOverTime: report.weight_over_time?.map((item) => ({
          date: this.formatDate(item.date),
          weight: item.weight_lbs,
        })) || [],
      },
    };
  }

  // Private transformation methods

  /**
   * Transform ticket DTO to card view model
   */
  private toTicketCardViewModel(ticket: WeightTicket): TicketCardViewModel {
    return {
      id: ticket.id,
      ticketNumber: ticket.ticket_number,
      jobId: ticket.job_id,
      jobDisplayName: ticket.job_name || `Job ${ticket.job_id}`,
      facilityName: ticket.facility_name,
      materialType: ticket.material_type,
      grossWeight: this.formatWeightLbs(ticket.gross_weight),
      tareWeight: this.formatWeightLbs(ticket.tare_weight),
      netWeight: this.formatWeightLbs(ticket.net_weight),
      weighedAt: this.formatTimestamp(ticket.weighed_at),
      weighedAtRelative: this.formatRelativeTime(ticket.weighed_at),
      statusBadge: this.getStatusBadge(ticket.status),
      hasPhoto: ticket.photos.length > 0,
      photoCount: ticket.photos.length,
      // TODO: Move permission logic to backend (Phase 2)
      // Backend should return can_edit, can_approve, can_reject based on
      // user roles, approval workflows, and business rules
      canEdit: ticket.status === 'pending',
      canApprove: ticket.status === 'pending',
      canReject: ticket.status === 'pending',
    };
  }

  /**
   * Transform ticket DTO to detail view model
   */
  private toTicketDetailViewModel(ticket: WeightTicket): TicketDetailViewModel {
    const card = this.toTicketCardViewModel(ticket);

    return {
      ...card,
      driverName: ticket.driver_name,
      vehicleNumber: ticket.vehicle_number,
      containerSize: ticket.container_size,
      wasteStream: this.formatWasteStream(ticket.material_type),
      facility: {
        name: ticket.facility_name,
        address: ticket.facility_address || '',
        certificationNumber: ticket.facility_certification,
      },
      photos: ticket.photos.map((photo) => ({
        id: photo.id,
        url: photo.url,
        thumbnailUrl: photo.thumbnail_url,
        caption: photo.caption,
      })),
      timeline: {
        createdAt: this.formatTimestamp(
          new Date(ticket.created_at * 1000).toISOString()
        ),
        submittedAt: ticket.submitted_at
          ? this.formatTimestamp(ticket.submitted_at)
          : undefined,
        reviewedAt: ticket.reviewed_at
          ? this.formatTimestamp(
              new Date(ticket.reviewed_at * 1000).toISOString()
            )
          : undefined,
        approvedBy: ticket.approved_by_name,
        rejectionReason: ticket.rejection_reason,
      },
      billingInfo: ticket.billing_rate
        ? {
            rate: `$${ticket.billing_rate.toFixed(2)}/ton`,
            amount: `$${this.calculateBillingAmount(
              ticket.net_weight,
              ticket.billing_rate
            )}`,
            invoiced: ticket.invoiced || false,
          }
        : undefined,
    };
  }

  // Helper methods for formatting and calculations

  private getStatusBadge(status: string): {
    label: string;
    color: string;
    icon: string;
  } {
    const statusMap: Record<
      string,
      { label: string; color: string; icon: string }
    > = {
      pending: { label: 'Pending Review', color: 'blue', icon: 'clock' },
      approved: { label: 'Approved', color: 'green', icon: 'check' },
      rejected: { label: 'Rejected', color: 'red', icon: 'x' },
    };

    return (
      statusMap[status] || {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        color: 'gray',
        icon: 'info',
      }
    );
  }

  private formatWeightLbs(weightLbs: number): string {
    return `${weightLbs.toLocaleString('en-US')} lbs`;
  }

  private formatWeight(weightLbs: number): string {
    // Convert to tons if over 2000 lbs
    if (weightLbs >= 2000) {
      const tons = weightLbs / 2000;
      return `${tons.toFixed(1)} tons`;
    }
    return this.formatWeightLbs(weightLbs);
  }

  private formatTimestamp(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return this.formatDate(dateString);
    }
  }

  private formatWasteStream(materialType: string): string {
    // TODO: Move waste stream classification to backend (Phase 2)
    // Backend should provide waste_stream field based on regulatory
    // classifications. This duplicates domain logic from backend.
    const streamMap: Record<string, string> = {
      'concrete': 'Construction & Demolition',
      'asphalt': 'Construction & Demolition',
      'wood': 'Construction & Demolition',
      'metal': 'Recyclables',
      'cardboard': 'Recyclables',
      'mixed': 'Mixed Waste',
    };

    return streamMap[materialType.toLowerCase()] || 'Other';
  }

  private calculateDaysElapsed(dateString: string): number {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  private calculateBillingAmount(netWeightLbs: number, ratePerTon: number): string {
    // TODO: Move billing calculation to backend (Phase 2)
    // Backend should provide billing_amount field calculated with proper
    // business rules (rounding, minimums, tiers, taxes)
    const tons = netWeightLbs / 2000;
    const amount = tons * ratePerTon;
    return amount.toFixed(2);
  }

  private capitalizeStatus(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  private buildFiltersViewModel(
    allTickets: WeightTicket[],
    activeFilters?: TicketFilters
  ): TicketFiltersViewModel {
    // Count tickets by status
    const statusCounts = allTickets.reduce(
      (acc, ticket) => {
        acc[ticket.status] = (acc[ticket.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    // Get unique jobs
    const uniqueJobs = Array.from(
      new Set(allTickets.map((t) => t.job_id))
    ).map((jobId) => {
      const ticket = allTickets.find((t) => t.job_id === jobId);
      return {
        value: jobId,
        label: ticket?.job_name || `Job ${jobId}`,
      };
    });

    return {
      statuses: [
        {
          value: 'all',
          label: 'All Tickets',
          count: allTickets.length,
          isActive: !activeFilters?.status || activeFilters.status === 'all',
        },
        {
          value: 'pending',
          label: 'Pending Review',
          count: statusCounts.pending || 0,
          isActive: activeFilters?.status === 'pending',
        },
        {
          value: 'approved',
          label: 'Approved',
          count: statusCounts.approved || 0,
          isActive: activeFilters?.status === 'approved',
        },
        {
          value: 'rejected',
          label: 'Rejected',
          count: statusCounts.rejected || 0,
          isActive: activeFilters?.status === 'rejected',
        },
      ],
      dateRanges: [
        {
          value: 'all',
          label: 'All Time',
          isActive:
            !activeFilters?.date_range || activeFilters.date_range === 'all',
        },
        {
          value: 'today',
          label: 'Today',
          isActive: activeFilters?.date_range === 'today',
        },
        {
          value: 'week',
          label: 'This Week',
          isActive: activeFilters?.date_range === 'week',
        },
        {
          value: 'month',
          label: 'This Month',
          isActive: activeFilters?.date_range === 'month',
        },
      ],
      jobs: uniqueJobs,
    };
  }

  private buildSummaryText(filtered: number, total: number): string {
    if (filtered === total) {
      return `Showing all ${total} ${total === 1 ? 'ticket' : 'tickets'}`;
    } else {
      return `Showing ${filtered} of ${total} ${total === 1 ? 'ticket' : 'tickets'}`;
    }
  }

  private getEmptyStateMessage(status?: string): string {
    if (!status || status === 'all') {
      return 'No weight tickets yet. Create your first ticket using the quick entry form.';
    }
    if (status === 'pending') {
      return 'No tickets pending review.';
    }
    return `No ${status} tickets found.`;
  }
}

// Export singleton instance
export const weightTicketApplicationService =
  new WeightTicketApplicationService();
